import { useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from '@mui/material';
import { apiGet } from '../../services/apiClient';
import { toast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

interface AsyncSelectProps<T> extends Omit<
    AutocompleteProps<T, boolean | undefined, boolean | undefined, boolean | undefined>,
    'options' | 'renderInput'
  > {
  url: string;
  label: string;
  value?: T | null;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  getOptionValue?: (option: T) => string | number;
  handleChange?: (value: T | null) => void;
}

const AsyncSelect = <T, >({
  url,
  label,
  value,
  disabled = false,
  getOptionLabel,
  getOptionValue,
  handleChange,
  error,
  helperText,
  size,
  ...props
}: AsyncSelectProps<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const res = await apiGet(url);
        const { result } = await res.json();
        setOptions(result);
      } catch (_) {
        toast.error(t('Failed to fetch options'));
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [url]);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={value || null}
      onChange={(event, newValue) => handleChange?.(newValue as T | null)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => getOptionValue ? getOptionValue(option) === getOptionValue(val) : option === val
      }
      disabled={disabled}
      autoHighlight
      openOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size={size}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
      }}
      {...props}
    />
  );
};

export default AsyncSelect;
